import { onBeforeUnmount, readonly, ref, shallowRef, unref, watch } from 'vue'
import {
  getEnterpriseReplayPolicy,
  getMeetingReplayPolicy,
  updateEnterpriseReplayPolicy,
  updateMeetingReplayPolicy,
} from '@/api/meetingReplay'

const SUCCESS_CODES = [0, 200, '0', '200']

function idempotencyKey() {
  if (typeof window !== 'undefined' && window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID()
  }
  return `replay-policy-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

function requiredResponseData(response) {
  if (!response || typeof response !== 'object' || !SUCCESS_CODES.includes(response.code) || response.data == null) {
    throw new Error('MEETING_REPLAY_POLICY_RESPONSE_INVALID')
  }
  return response.data
}

function isVersionConflict(error) {
  const details = [error?.code, error?.msg, error?.message, error?.errorCode, error?.data?.errorCode]
    .filter(Boolean)
    .join(' ')
  return details.includes('POLICY_VERSION_CONFLICT')
}

export function useMeetingReplayPolicy(meetingCode, { canManageMeetingPolicy, canManageEnterprisePolicy } = {}) {
  const meetingPolicy = ref(null)
  const enterprisePolicy = ref(null)
  const meetingLoading = shallowRef(false)
  const enterpriseLoading = shallowRef(false)
  const meetingSaving = shallowRef(false)
  const enterpriseSaving = shallowRef(false)
  const meetingError = shallowRef(false)
  const enterpriseError = shallowRef(false)
  const meetingVersionConflict = shallowRef(false)
  const enterpriseVersionConflict = shallowRef(false)
  let scopeVersion = 0
  let meetingReadSequence = 0
  let enterpriseReadSequence = 0
  let meetingSaveSequence = 0
  let enterpriseSaveSequence = 0

  const currentCode = () => String(unref(meetingCode) || '').trim()
  const canManageMeeting = () => !!unref(canManageMeetingPolicy)
  const canManageEnterprise = () => !!unref(canManageEnterprisePolicy)

  const beginRead = (scope) => ({
    code: currentCode(),
    requestId: scope === 'meeting' ? ++meetingReadSequence : ++enterpriseReadSequence,
    scope,
    scopeVersion,
  })

  const isCurrentRead = (context) =>
    context.scopeVersion === scopeVersion &&
    context.code === currentCode() &&
    context.requestId === (context.scope === 'meeting' ? meetingReadSequence : enterpriseReadSequence)

  const beginSave = (scope) => {
    if (scope === 'meeting') meetingReadSequence += 1
    else enterpriseReadSequence += 1
    return {
      code: currentCode(),
      requestId: scope === 'meeting' ? ++meetingSaveSequence : ++enterpriseSaveSequence,
      scope,
      scopeVersion,
    }
  }

  const isCurrentSave = (context) =>
    context.scopeVersion === scopeVersion &&
    context.code === currentCode() &&
    context.requestId === (context.scope === 'meeting' ? meetingSaveSequence : enterpriseSaveSequence)

  const loadMeetingPolicy = async ({ silent = false } = {}) => {
    const code = currentCode()
    if (!code || !canManageMeeting()) {
      meetingPolicy.value = null
      return null
    }
    const context = beginRead('meeting')
    if (!silent) meetingLoading.value = true
    try {
      const response = await getMeetingReplayPolicy(code)
      const policy = requiredResponseData(response)
      if (!isCurrentRead(context)) return null
      meetingPolicy.value = policy
      meetingError.value = false
      return policy
    } catch (error) {
      if (isCurrentRead(context)) meetingError.value = true
      throw error
    } finally {
      if (isCurrentRead(context)) meetingLoading.value = false
    }
  }

  const loadEnterprisePolicy = async ({ silent = false } = {}) => {
    const code = currentCode()
    if (!code || !canManageEnterprise()) {
      enterprisePolicy.value = null
      return null
    }
    const context = beginRead('enterprise')
    if (!silent) enterpriseLoading.value = true
    try {
      const response = await getEnterpriseReplayPolicy(code)
      const policy = requiredResponseData(response)
      if (!isCurrentRead(context)) return null
      enterprisePolicy.value = policy
      enterpriseError.value = false
      return policy
    } catch (error) {
      if (isCurrentRead(context)) enterpriseError.value = true
      throw error
    } finally {
      if (isCurrentRead(context)) enterpriseLoading.value = false
    }
  }

  const refresh = () => {
    const requests = []
    if (canManageMeeting()) requests.push(loadMeetingPolicy())
    if (canManageEnterprise()) requests.push(loadEnterprisePolicy())
    return Promise.allSettled(requests)
  }

  const saveMeetingPolicy = async (overrideMode) => {
    const code = currentCode()
    const expectedVersion = meetingPolicy.value?.meetingPolicyVersion
    if (!code || !canManageMeeting() || expectedVersion == null) {
      throw new Error('MEETING_REPLAY_MEETING_POLICY_REQUIRED')
    }
    if (meetingSaving.value) throw new Error('MEETING_REPLAY_POLICY_ACTION_IN_PROGRESS')
    const context = beginSave('meeting')
    meetingSaving.value = true
    meetingVersionConflict.value = false
    try {
      const response = await updateMeetingReplayPolicy(code, { overrideMode, expectedVersion }, idempotencyKey())
      const policy = requiredResponseData(response)
      if (!isCurrentSave(context)) return null
      meetingPolicy.value = policy
      meetingError.value = false
      return policy
    } catch (error) {
      if (isCurrentSave(context)) {
        meetingError.value = true
        if (isVersionConflict(error)) {
          await loadMeetingPolicy({ silent: true }).catch(() => {})
          meetingVersionConflict.value = true
        }
      }
      throw error
    } finally {
      if (isCurrentSave(context)) meetingSaving.value = false
    }
  }

  const saveEnterprisePolicy = async (defaultMode) => {
    const code = currentCode()
    const expectedVersion = enterprisePolicy.value?.enterprisePolicyVersion
    if (!code || !canManageEnterprise() || expectedVersion == null) {
      throw new Error('MEETING_REPLAY_ENTERPRISE_POLICY_REQUIRED')
    }
    if (enterpriseSaving.value) throw new Error('MEETING_REPLAY_POLICY_ACTION_IN_PROGRESS')
    const context = beginSave('enterprise')
    enterpriseSaving.value = true
    enterpriseVersionConflict.value = false
    try {
      const response = await updateEnterpriseReplayPolicy(code, { defaultMode, expectedVersion }, idempotencyKey())
      const policy = requiredResponseData(response)
      if (!isCurrentSave(context)) return null
      enterprisePolicy.value = policy
      enterpriseError.value = false
      await loadMeetingPolicy({ silent: true }).catch(() => {})
      return policy
    } catch (error) {
      if (isCurrentSave(context)) {
        enterpriseError.value = true
        if (isVersionConflict(error)) {
          await loadEnterprisePolicy({ silent: true }).catch(() => {})
          await loadMeetingPolicy({ silent: true }).catch(() => {})
          enterpriseVersionConflict.value = true
        }
      }
      throw error
    } finally {
      if (isCurrentSave(context)) enterpriseSaving.value = false
    }
  }

  watch(
    [currentCode, canManageMeeting, canManageEnterprise],
    () => {
      scopeVersion += 1
      meetingReadSequence += 1
      enterpriseReadSequence += 1
      meetingSaveSequence += 1
      enterpriseSaveSequence += 1
      meetingPolicy.value = null
      enterprisePolicy.value = null
      meetingLoading.value = false
      enterpriseLoading.value = false
      meetingSaving.value = false
      enterpriseSaving.value = false
      meetingError.value = false
      enterpriseError.value = false
      meetingVersionConflict.value = false
      enterpriseVersionConflict.value = false
      refresh().catch(() => {})
    },
    { immediate: true }
  )

  onBeforeUnmount(() => {
    scopeVersion += 1
    meetingReadSequence += 1
    enterpriseReadSequence += 1
    meetingSaveSequence += 1
    enterpriseSaveSequence += 1
  })

  return {
    meetingPolicy: readonly(meetingPolicy),
    enterprisePolicy: readonly(enterprisePolicy),
    meetingLoading: readonly(meetingLoading),
    enterpriseLoading: readonly(enterpriseLoading),
    meetingSaving: readonly(meetingSaving),
    enterpriseSaving: readonly(enterpriseSaving),
    meetingError: readonly(meetingError),
    enterpriseError: readonly(enterpriseError),
    meetingVersionConflict: readonly(meetingVersionConflict),
    enterpriseVersionConflict: readonly(enterpriseVersionConflict),
    refresh,
    saveMeetingPolicy,
    saveEnterprisePolicy,
  }
}
